import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modalidad, Dificultad } from '../types';
import useRandomGlowEffect from '../hooks/useRandomGlowEffect';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from '../components/LoginModal';
import RegisterModal from '../components/RegisterModal';
import { FALLBACK_PROFILE_IMAGE } from '../constants/profileDefaults';
import MainHeader from '../components/MainHeader';

const Lobby: React.FC = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  useRandomGlowEffect();
  const [paso, setPaso] = useState<'modalidad' | 'dificultad' | 'nickname'>('modalidad');
  const [modalidad, setModalidad] = useState<Modalidad | null>(null);
  const [dificultad, setDificultad] = useState<Dificultad | null>(null);
  const [nickname, setNickname] = useState('');
  const [errorNick, setErrorNick] = useState('');
  const [hoverDif, setHoverDif] = useState<'normal' | 'dificil' | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  const [mobileSelDif, setMobileSelDif] = useState<'normal' | 'dificil' | null>(null);
  const [isMobile] = useState(() => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));

  const handleDificultadClick = (d: Dificultad) => {
    if (isMobile) {
      if (mobileSelDif === d) {
        elegirDificultad(d);
      } else {
        setMobileSelDif(d);
        setHoverDif(d);
      }
    } else {
      elegirDificultad(d);
    }
  };

  const elegirModalidad = (m: Modalidad) => {
    setModalidad(m);
    setPaso('dificultad');
  };

  const elegirDificultad = (d: Dificultad) => {
    setDificultad(d);
    setPaso('nickname');
  };

  useEffect(() => {
    if (user && paso === 'nickname') {
      setNickname(user.nickname);
      setErrorNick('');
    }
  }, [user, paso]);

  const generarNicknameInvitado = () => `Player#${Math.floor(100000 + Math.random() * 900000)}`;

  const iniciarPartida = (nicknameForzado?: string) => {
    const nick = (nicknameForzado ?? (user?.nickname || nickname)).trim();
    if (!nick || nick.length < 2) {
      setErrorNick('El nombre debe tener al menos 2 caracteres');
      return;
    }
    if (nick.length > 20) {
      setErrorNick('El nombre no puede superar 20 caracteres');
      return;
    }
    navigate('/game', { state: { modalidad, dificultad, nickname: nick } });
  };

  const handleAuthSuccess = (token: string, authUser: any) => {
    login(token, authUser);
    setShowLoginModal(false);
    setShowRegisterModal(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleAuthSuccess}
      />
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onRegisterSuccess={handleAuthSuccess}
      />
      <MainHeader />

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-3xl">

          {/* PASO 1: Elegir modalidad */}
          {paso === 'modalidad' && (
            <div className="animate-fade-in">
              <h2 className="font-gaming text-base text-gaming-cyan text-center mb-2 tracking-widest">PASO 1 DE 3</h2>
              <h1 className="font-gaming text-2xl text-white text-center mb-7">ELIGE TU MODALIDAD</h1>
              <div className="flex justify-center mb-8">
                <button onClick={() => navigate('/')} className="text-white/40 hover:text-white transition-colors font-main text-base">
                  ← Volver
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Classic */}
                <button
                  onClick={() => elegirModalidad('classic')}
                  className="card-gaming text-left hover:border-gaming-purple/60 hover:bg-gaming-purple/10 transition-all duration-200 group"
                >
                  <img
                    src="/logos/mgletras-logo.png"
                    alt="Main Gamer Letras"
                    className="main-gamer-logo-classic mb-4"
                    style={{ height: isMobile ? '32px' : '40px' }}
                  />
                  <h3 className="font-gaming text-gaming-purple text-sm mb-3">CLASSIC</h3>
                  <p className="font-main text-white/70 text-base leading-relaxed">
                    La experiencia completa del concurso: <br />4 niveles: trivias, portadas, personajes y canciones. <br />
                    Sin tiempo límite — responde a tu ritmo.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-sm bg-gaming-purple/20 text-gaming-purple border border-gaming-purple/70 px-3 py-1 rounded font-main font-bold">5 trivias</span>
                    <span className="text-sm bg-gaming-purple/20 text-gaming-purple border border-gaming-purple/70 px-3 py-1 rounded font-main font-bold">8 portadas</span>
                    <span className="text-sm bg-gaming-purple/20 text-gaming-purple border border-gaming-purple/70 px-3 py-1 rounded font-main font-bold">5 personajes</span>
                    <span className="text-sm bg-gaming-purple/20 text-gaming-purple border border-gaming-purple/70 px-3 py-1 rounded font-main font-bold">5 canciones</span>
                  </div>
                </button>

                {/* No Hit */}
                <button
                  onClick={() => elegirModalidad('nohit')}
                  className="card-gaming text-left hover:border-gaming-pink/60 hover:bg-gaming-pink/10 transition-all duration-200 group"
                >
                  <img
                    src="/logos/skull.png"
                    alt="No Hit"
                    className="nohit-mode-icon mb-4"
                    style={{ height: isMobile ? '32px' : '40px' }}
                  />
                  <h3 className="font-gaming text-gaming-pink text-sm mb-3">NO HIT</h3>
                  <p className="font-main text-white/70 text-base leading-relaxed">
                    Preguntas aleatorias de todos los niveles sin parar. Solo hay una regla: 
                    <span className="text-gaming-pink font-bold"> un fallo y se acabó</span>. 
                    ¿Hasta dónde puedes llegar?
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-sm bg-gaming-pink/20 text-gaming-pink border border-gaming-pink/70 px-3 py-1 rounded font-main font-bold">Niveles mezclados</span>
                    <span className="text-sm bg-gaming-pink/20 text-gaming-pink border border-gaming-pink/70 px-3 py-1 rounded font-main font-bold">Sin límite</span>
                    <span className="text-sm bg-gaming-pink/20 text-gaming-pink border border-gaming-pink/70 px-3 py-1 rounded font-main font-bold">0 fallos</span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* PASO 2: Elegir dificultad */}
          {paso === 'dificultad' && (
            <div className="animate-fade-in">
              <h2 className="font-gaming text-base text-gaming-cyan text-center mb-2 tracking-widest">PASO 2 DE 3</h2>
              <h1 className="font-gaming text-2xl text-white text-center mb-2">ELIGE DIFICULTAD</h1>
              <p className="font-main text-white/50 text-center text-lg mb-4">
                Modo: <span className="text-gaming-purple font-bold">{modalidad === 'classic' ? 'Classic' : 'No Hit'}</span>
              </p>
              <div className="flex justify-center mb-8">
                <button onClick={() => setPaso('modalidad')} className="text-white/40 hover:text-white transition-colors font-main text-base">
                  ← Volver
                </button>
              </div>
              {isMobile && mobileSelDif && (
                <div className="text-center mb-6 px-4 py-2 bg-gaming-pink/10 border border-gaming-pink/30 rounded-xl max-w-sm mx-auto animate-pulse">
                  <p className="font-gaming text-gaming-pink text-xs tracking-wider">
                    Confirma la dificultad pulsando de nuevo encima de ella
                  </p>
                </div>
              )}
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${modalidad === 'classic' && hoverDif ? 'items-start' : 'items-stretch'}`}>
                {/* Normal */}
                <button
                  onClick={() => handleDificultadClick('normal')}
                  onMouseEnter={() => !isMobile && setHoverDif('normal')}
                  onMouseLeave={() => !isMobile && setHoverDif(null)}
                  className={`card-gaming text-left transition-all duration-200 ${
                    isMobile && mobileSelDif === 'normal'
                      ? 'border-gaming-cyan bg-gaming-cyan/10 shadow-[0_0_15px_rgba(34,211,238,0.45)]'
                      : 'hover:border-gaming-cyan/60 hover:bg-gaming-cyan/10'
                  }`}
                >
                  <img
                    src="/logos/choose.png"
                    alt="Dificultad normal"
                    className="difficulty-mode-icon difficulty-mode-icon-normal mb-4"
                    style={{ height: '40px' }}
                  />
                  <h3 className="font-gaming text-gaming-cyan text-sm mb-3">NORMAL</h3>
                  <p className="font-main text-white/70 text-base leading-relaxed">
                    Siempre tendrás <span className="text-gaming-cyan font-bold">4 opciones</span> para elegir en todos los niveles. Ideal para empezar o si eres más casual en videojuegos.
                  </p>
                  <div className="mt-4 mb-1">
                    <span className="badge-normal">Normal</span>
                  </div>
                  {/* Desglose puntuación — solo en Classic */}
                  <div className={`overflow-hidden ${modalidad === 'classic' && hoverDif === 'normal' ? 'max-h-64 transition-all duration-300 ease-in-out' : 'max-h-0'}`}>
                    <div className="border-t border-white/20 mt-4 pt-4 space-y-2">
                      <p className="font-gaming text-white/60 text-xs mb-3 tracking-widest">PUNTUACIÓN POR NIVEL</p>
                      {[
                        { label: 'Trivia', desc: 'Respuesta correcta', pts: '70 pts' },
                        { label: 'Portada', desc: 'Título del juego', pts: '65 pts' },
                        { label: 'Personaje', desc: 'Nombre (saga indicada)', pts: '80 pts' },
                        { label: 'Canción', desc: 'Título del juego', pts: '85 pts' },
                      ].map(row => (
                        <div key={row.label} className="flex items-center justify-between text-sm font-main">
                          <div>
                            <span className="text-white/90">{row.label}</span>
                            <span className="text-white/50 ml-2 text-xs">{row.desc}</span>
                          </div>
                          <span className="text-gaming-cyan font-bold ml-4 shrink-0">{row.pts}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </button>

                {/* Difícil */}
                <button
                  onClick={() => handleDificultadClick('dificil')}
                  onMouseEnter={() => !isMobile && setHoverDif('dificil')}
                  onMouseLeave={() => !isMobile && setHoverDif(null)}
                  className={`card-gaming text-left transition-all duration-200 ${
                    isMobile && mobileSelDif === 'dificil'
                      ? 'border-gaming-pink bg-gaming-pink/10 shadow-[0_0_15px_rgba(236,72,153,0.45)]'
                      : 'hover:border-gaming-pink/60 hover:bg-gaming-pink/10'
                  }`}
                >
                  <img
                    src="/logos/spartan.png"
                    alt="Dificultad difícil"
                    className="difficulty-mode-icon difficulty-mode-icon-dificil mb-4"
                    style={{ height: '36px' }}
                  />
                  <h3 className="font-gaming text-gaming-pink text-sm mb-3">DIFÍCIL</h3>
                  <p className="font-main text-white/70 text-base leading-relaxed">
                    Solo el <span className="text-gaming-pink font-bold">nivel 1</span> tendrá 4 opciones. El resto de niveles son <span className="text-gaming-pink font-bold">respuesta libre</span> — sin pistas, sin opciones. Para los que de verdad se consideran gamers.
                  </p>
                  <div className="mt-4 mb-1">
                    <span className="badge-dificil">Difícil</span>
                  </div>
                  {/* Desglose puntuación — solo en Classic */}
                  <div className={`overflow-hidden ${modalidad === 'classic' && hoverDif === 'dificil' ? 'max-h-[600px] transition-all duration-300 ease-in-out' : 'max-h-0'}`}>
                    <div className="border-t border-white/20 mt-4 pt-4 space-y-4">
                      <p className="font-gaming text-white/60 text-xs tracking-widest">PUNTUACIÓN PARCIAL POR NIVEL</p>

                      {/* Trivia */}
                      <div className="text-sm font-main space-y-1">
                        <p className="text-white font-bold">Trivia</p>
                        <div className="pl-3 space-y-0.5">
                          <p className="text-white/70">Correcta <span className="text-gaming-pink font-bold float-right">90 pts</span></p>
                        </div>
                      </div>

                      {/* Portada */}
                      <div className="text-sm font-main space-y-1">
                        <p className="text-white font-bold">Portada</p>
                        <div className="pl-3 space-y-0.5">
                          <p className="text-white/70">Saga + Título <span className="text-gaming-pink font-bold float-right">130 pts</span></p>
                          <p className="text-white/70">Solo saga <span className="text-white/80 font-bold float-right">90 pts</span></p>
                          <p className="text-white/50 text-xs italic">Independiente: Título <span className="text-white/70 font-bold not-italic float-right">110 pts</span></p>
                        </div>
                      </div>

                      {/* Personaje */}
                      <div className="text-sm font-main space-y-1">
                        <p className="text-white font-bold">Personaje</p>
                        <p className="text-white/50 uppercase tracking-wider text-xs pl-3 pt-1">Saga independiente</p>
                        <div className="pl-3 space-y-0.5">
                          <p className="text-white/70">Juego + Nombre <span className="text-gaming-pink font-bold float-right">140 pts</span></p>
                          <p className="text-white/70">Solo juego <span className="text-white/80 font-bold float-right">90 pts</span></p>
                          <p className="text-white/70">Solo nombre <span className="text-white/80 font-bold float-right">50 pts</span></p>
                        </div>
                        <p className="text-white/50 uppercase tracking-wider text-xs pl-3 pt-1">Saga no independiente</p>
                        <div className="pl-3 space-y-0.5">
                          <p className="text-white/70">Saga + Juego específico <span className="text-white/80 font-bold float-right">110 pts</span></p>
                          <p className="text-white/70">Solo saga <span className="text-white/80 font-bold float-right">80 pts</span></p>
                          <p className="text-white/70">Solo juego específico <span className="text-white/80 font-bold float-right">30 pts</span></p>
                        </div>
                      </div>

                      {/* Canción */}
                      <div className="text-sm font-main space-y-1">
                        <p className="text-white font-bold">Canción</p>
                        <p className="text-white/50 uppercase tracking-wider text-xs pl-3 pt-1">Saga independiente</p>
                        <div className="pl-3 space-y-0.5">
                          <p className="text-white/70">Título exacto <span className="text-gaming-pink font-bold float-right">140 pts</span></p>
                        </div>
                        <p className="text-white/50 uppercase tracking-wider text-xs pl-3 pt-1">Saga no independiente</p>
                        <div className="pl-3 space-y-0.5">
                          <p className="text-white/70">Saga + Título exacto <span className="text-gaming-pink font-bold float-right">180 pts</span></p>
                          <p className="text-white/70">Solo saga <span className="text-white/80 font-bold float-right">100 pts</span></p>
                        </div>
                      </div>

                    </div>
                  </div>
                </button>
              </div>

            </div>
          )}

          {/* PASO 3: Nickname / Sesion */}
          {paso === 'nickname' && (
            <div className="animate-fade-in max-w-lg mx-auto">
              <h2 className="font-gaming text-base text-gaming-cyan text-center mb-2 tracking-widest">PASO 3 DE 3</h2>
              <h1 className="font-gaming text-2xl text-white text-center mb-2">
                {user ? 'TODO LISTO PARA JUGAR' : 'ELIGE CÓMO QUIERES JUGAR'}
              </h1>
              <p className="font-main text-white/50 text-center text-lg mb-8">
                <span className="text-gaming-purple">{modalidad === 'classic' ? 'Classic' : 'No Hit'}</span>
                {' · '}
                <span className={dificultad === 'dificil' ? 'text-gaming-pink' : 'text-gaming-cyan'}>
                  {dificultad === 'dificil' ? 'Difícil' : 'Normal'}
                </span>
              </p>

              <button onClick={() => setPaso('dificultad')} className="-mt-4 mb-6 text-white/40 hover:text-white transition-colors font-main text-base block mx-auto">
                ← Volver
              </button>

              {user ? (
                <div className="card-gaming">
                  <div className="flex flex-col items-center text-center mb-6">
                    <div className="w-20 h-20 rounded-full bg-gaming-purple/20 border border-gaming-purple/50 overflow-hidden flex items-center justify-center mb-4">
                      <img src={user.fotoPerfil || FALLBACK_PROFILE_IMAGE} alt={user.nickname} className="w-[80%] h-[80%] object-contain" />
                    </div>
                    <p className="font-gaming text-white text-lg tracking-wider">{user.nickname}</p>
                  </div>
                  <button onClick={() => iniciarPartida(user.nickname)} className="btn-primary w-full font-gaming text-sm tracking-wider py-4">
                    COMENZAR PARTIDA
                  </button>
                </div>
              ) : (
                <div className="card-gaming space-y-5">
                  <div>
                    <h3 className="font-gaming text-gaming-cyan text-sm mb-2 tracking-wider">OPCIÓN 1</h3>
                    <p className="font-main text-white/60 text-sm mb-4">
                      Juega ahora mismo como invitado y se te asignará automáticamente un nickname del tipo Player#XXXXXX.
                    </p>
                    <button onClick={() => iniciarPartida(generarNicknameInvitado())} className="btn-primary w-full font-gaming text-sm tracking-wider py-4">
                      JUGAR COMO INVITADO
                    </button>
                  </div>

                  <div className="border-t border-white/10 pt-5">
                    <h3 className="font-gaming text-gaming-purple text-sm mb-2 tracking-wider">OPCIÓN 2</h3>
                    <p className="font-main text-white/60 text-sm mb-4">
                      Inicia sesión o regístrate para jugar con el nickname de tu perfil y guardar tus datos correctamente. Además, podrás acceder a tu perfil, ver tu progreso, tus estadísticas y competir en el ranking global.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        onClick={() => {
                          setShowRegisterModal(false);
                          setShowLoginModal(true);
                        }}
                        className="btn-neon-login py-3"
                      >
                        INICIAR SESIÓN
                      </button>
                      <button
                        onClick={() => {
                          setShowLoginModal(false);
                          setShowRegisterModal(true);
                        }}
                        className="btn-neon-register py-3"
                      >
                        REGISTRARSE
                      </button>
                    </div>
                  </div>

                  {errorNick && <p className="text-gaming-red text-sm font-main">{errorNick}</p>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Lobby;
